import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import Cookies from 'universal-cookie';
import { startTokenRefreshMonitor, stopTokenRefreshMonitor } from '../utils/CognitoCustomChallengeService';
import { startAuthPortalSilentRefresh, stopAuthPortalSilentRefresh } from '../utils/authPortalAuth';
import axiosInstance from '../api/axiosInstance';
import { API_ENDPOINTS } from '../api/apiConfig';
import dialogEventEmitter from '../utils/dialogEventEmitter';
import RemoveCookies, { clearCookies } from '../RemoveCookies';
import { redirectToAuthPortalLogout } from '../utils/authPortal';
import roleManagement from '../utils/roleManagement';

const useAuthPortalRefresh = () => !!process.env.authUrl;
const cookies = new Cookies();
const SUPPORT_ACCESS_LEVELS = ['s1', 's2', 's3'];
const DEFAULT_SYNC_LEAD_SECONDS = 60;
const LOCAL_COUNTDOWN_INTERVAL_MS = 1000;
const IMPERSONATION_POLL_INTERVAL_MS = 10000;
const IMPERSONATION_PENDING_CODE = '44034';
const TIMER_LOG_INTERVAL_MS = 1000;
const TIMER_LOG_STORAGE_KEY = 'supportSessionTimerDebug';

function isSupportSessionSubject() {
  if (typeof window === 'undefined') return false;
  const accessLevel = localStorage.getItem('accessLevel');
  return SUPPORT_ACCESS_LEVELS.includes(accessLevel) || !!cookies.get('userSubRole');
}

function getSupportSessionUserId() {
  return localStorage.getItem('supportAgentUserId') || cookies.get('_Id');
}

function shouldMonitorSupportSessionActivity() {
  // isSupportSessionSubject stays first: it carries this function's `typeof window` guard, which
  // is what makes the window.location read below safe.
  if (!isSupportSessionSubject()) return false;
  const path = window.location.pathname || '';
  if (path === '/support/usersList' || path.endsWith('/support/usersList')) return false;
  const blocked = ['/', '/failSafeSupport', '/landingPage', '/muiSupport', '/premiumUser'];
  return !blocked.some((b) => path === b);
}

/** True while this browser is inside a customer account rather than the agent's own. */
function isImpersonatingCustomer() {
  return roleManagement.getUserSubRole() === 'supportAgent';
}

function fullName(firstName, lastName) {
  return [firstName, lastName].filter(Boolean).join(' ').trim();
}

/**
 * Leaves the customer account after handing the session over. The server has already ended this
 * agent's impersonation, so a full reload lets CamSDk put them back on the support user list
 * instead of a customer page they no longer have access to.
 */
function leaveImpersonatedAccount() {
  localStorage.removeItem('userToLogin');
  localStorage.removeItem('goToMyAcc');
  const configured = String(import.meta.env.VITE_APP_BASE_NAME || '')
    .trim()
    .replace(/\/+$/, '');
  // '/' collapses to empty, and a value written without its leading slash still has to join the
  // origin as a path rather than running into the host name.
  const basename = !configured || configured.startsWith('/') ? configured : `/${configured}`;
  window.location.href = `${window.location.origin}${basename}/`;
}

function isSessionExpiredResponse(responseCode) {
  return responseCode === '44030' || responseCode === 44030;
}

function isSessionWarningResponse(responseCode) {
  return responseCode === '44033' || responseCode === 44033;
}

function isSessionActivitySuccess(result) {
  const responseCode = result?.response?.responseCode;
  return (
    result?.response?.status === true || responseCode === '9000' || responseCode === 9000 || responseCode === '200' || responseCode === 200
  );
}

function getSessionTtlFromApiResult(result) {
  const details = result?.details || result?.info || result?.data || {};
  if (details.sessionSecondsRemaining === undefined) {
    return null;
  }

  const ttl = Number(details.sessionSecondsRemaining);
  if (!Number.isFinite(ttl)) {
    return null;
  }

  return {
    sessionSecondsRemaining: ttl,
    sessionWarningActive: details.sessionWarningActive === '1' || details.sessionWarningActive === 1,
    sessionTtlSyncLeadSeconds: Number(details.sessionTtlSyncLeadSeconds) || DEFAULT_SYNC_LEAD_SECONDS
  };
}

function parseSessionActivityResponse(result) {
  if (!result) return null;
  return result?.response?.responseCode ?? result?.responseCode;
}

function isSupportAccessLevelUser() {
  const accessLevel = localStorage.getItem('accessLevel');
  return SUPPORT_ACCESS_LEVELS.includes(accessLevel);
}

/** Timer log: on in a dev build, or wherever the key has been set by hand. */
function isTimerLogEnabled() {
  try {
    if (localStorage.getItem(TIMER_LOG_STORAGE_KEY) === '1') return true;
  } catch {
    // localStorage can throw in private browsing.
  }
  return !!import.meta.env?.DEV;
}

/** Timer log: the server's remaining seconds. fetch, so axiosInstance's interceptor never sees it. */
async function readBackendTtl(userId) {
  const url = API_ENDPOINTS.GET_SUPPORT_SESSION_ACTIVITY.url.replace('{_Id}', userId);
  const headers = { apikey: process.env.apiKey, accountId: cookies.get('accountId') };
  if (!process.env.authUrl) {
    const accessToken = localStorage.getItem('token') || localStorage.getItem('access_token');
    if (accessToken) headers.token = accessToken;
  }
  // AbortSignal.timeout is optional-called: unsupported it yields undefined, which fetch ignores.
  const response = await fetch(url, {
    method: 'GET',
    headers,
    credentials: 'include',
    signal: AbortSignal.timeout?.(10000)
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
}

let sessionExpiryInProgress = false;
async function handleSupportSessionExpired() {
  if (sessionExpiryInProgress) return;
  sessionExpiryInProgress = true;
  try {
    const isImpersonating = roleManagement.getUserSubRole() === 'supportAgent';
    const isSupportUser = isSupportAccessLevelUser();

    if (isImpersonating || isSupportUser) {
      // An inactivity timeout is a real session timeout, so sign the agent out completely
      // rather than dropping them on the support user list. Landing on another
      // authenticated page left a valid token in place and let the login bootstrap mint a
      // fresh Redis TTL, which is what produced the expire -> redirect -> reload loop.
      // Same path as the Logout menu item: end the support session, clear cookies and
      // localStorage, then hand off to the auth portal logout, which returns to sign-in.
      await RemoveCookies.removeAccCookies();
      redirectToAuthPortalLogout();
      return;
    }
    dialogEventEmitter.emit('showDialog', {
      title: '44030',
      content: '44030',
      calledFrom: 'useApi',
      modalClass: 'techSupportSessionOutModal'
    });
  } catch {
    // Never leave the agent half-logged-out: hard clear and hand off to the portal.
    clearCookies();
    redirectToAuthPortalLogout();
  } finally {
    window.setTimeout(() => {
      sessionExpiryInProgress = false;
    }, 5000);
  }
}

/**
 * TokenRefreshMonitor Component
 * - Auth portal (authUrl): silent refresh every 5 minutes so user never sees token expiration.
 * - Cognito: monitors token expiry and refreshes 2 minutes before expiration.
 * - Support session: TTL-based sync - schedules one API call when TTL - 60s is reached.
 *   No continuous polling; only calls sync API when local timer fires.
 * This component should be included in the app to ensure tokens stay fresh.
 */
export default function TokenRefreshMonitor() {
  const isAuthPortal = useAuthPortalRefresh();

  // Support session warning state
  const [warningOpen, setWarningOpen] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(0);
  const [continueLoading, setContinueLoading] = useState(false);
  const [countdownRestartKey, setCountdownRestartKey] = useState(0);

  // Incoming hand-over request from another support agent for the account being impersonated.
  const [incomingRequest, setIncomingRequest] = useState(null);
  const [decisionLoading, setDecisionLoading] = useState(false);

  useEffect(() => {
    warningOpenRef.current = warningOpen;
  }, [warningOpen]);

  // Refs for timers
  const syncTimeoutRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  /** When the session actually ends, so the countdown can be read off a clock rather than ticks. */
  const expiresAtRef = useRef(null);
  const continueInFlightRef = useRef(false);
  const syncLeadSecondsRef = useRef(DEFAULT_SYNC_LEAD_SECONDS);
  const warningOpenRef = useRef(false);
  const decisionInFlightRef = useRef(false);

  // Clear sync timeout
  const clearSyncTimeout = useCallback(() => {
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
      syncTimeoutRef.current = null;
    }
  }, []);

  // Clear countdown interval
  const clearCountdownInterval = useCallback(() => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  const callSyncApi = useCallback(
    async (_source = 'timer') => {
      if (!shouldMonitorSupportSessionActivity()) return;
      const userId = getSupportSessionUserId();
      if (!userId) return;

      try {
        const url = API_ENDPOINTS.GET_SUPPORT_SESSION_ACTIVITY.url.replace('{_Id}', userId);
        const response = await axiosInstance.get(url);
        const result = response.data;
        const responseCode = parseSessionActivityResponse(result);

        if (isSessionExpiredResponse(responseCode)) {
          setWarningOpen(false);
          clearCountdownInterval();
          await handleSupportSessionExpired();
          return;
        }

        if (isSessionWarningResponse(responseCode)) {
          const details = result?.details || {};
          const ttl = Number(details.sessionSecondsRemaining) || 0;
          expiresAtRef.current = Date.now() + ttl * 1000;
          setWarningOpen(true);
          setSecondsRemaining(ttl);
          return;
        }

        const ttlInfo = getSessionTtlFromApiResult(result);
        if (ttlInfo && (ttlInfo.sessionWarningActive || ttlInfo.sessionSecondsRemaining > 0)) {
          dialogEventEmitter.emit('supportSessionTtlUpdate', ttlInfo);
          return;
        }
      } catch (error) {
        const errorCode = parseSessionActivityResponse(error?.response?.data);
        if (isSessionExpiredResponse(errorCode)) {
          setWarningOpen(false);
          clearCountdownInterval();
          await handleSupportSessionExpired();
          return;
        }
        console.warn('[SupportSession] sessionactivity failed:', error?.message || 'Unknown error');
      }
    },
    [clearCountdownInterval]
  );

  const bootstrapSupportSession = useCallback(async () => {
    if (!shouldMonitorSupportSessionActivity()) return;
    if (syncTimeoutRef.current || warningOpenRef.current) return;
    await callSyncApi('bootstrap');
  }, [callSyncApi]);

  const scheduleSyncTimer = useCallback(
    (ttlInfo) => {
      clearSyncTimeout();
      clearCountdownInterval();

      if (!isSupportSessionSubject()) return;

      const { sessionSecondsRemaining, sessionWarningActive, sessionTtlSyncLeadSeconds } = ttlInfo;
      const syncLeadSeconds = sessionTtlSyncLeadSeconds || DEFAULT_SYNC_LEAD_SECONDS;
      syncLeadSecondsRef.current = syncLeadSeconds;
      const ttl = Number(sessionSecondsRemaining);

      if ((!Number.isFinite(ttl) || ttl <= 0) && !sessionWarningActive) {
        setWarningOpen(false);
        setSecondsRemaining(0);
        return;
      }

      if (sessionWarningActive || (ttl > 0 && ttl <= syncLeadSeconds)) {
        expiresAtRef.current = Date.now() + Math.max(ttl, 0) * 1000;
        setWarningOpen(true);
        setSecondsRemaining(Math.max(ttl, 0));
        return;
      }

      // Schedule sync API call at (TTL - syncLeadSeconds)
      const delayMs = (ttl - syncLeadSeconds) * 1000;

      if (delayMs > 0) {
        syncTimeoutRef.current = setTimeout(() => {
          syncTimeoutRef.current = null;
          void callSyncApi('timer');
        }, delayMs);
      } else {
        void callSyncApi('timer');
      }

      // Hide warning modal if it was open
      setWarningOpen(false);
      setSecondsRemaining(0);
    },
    [clearSyncTimeout, clearCountdownInterval, callSyncApi]
  );

  const handleContinueSession = useCallback(async () => {
    if (continueInFlightRef.current || !isSupportSessionSubject()) return;

    const userId = getSupportSessionUserId();
    if (!userId) return;

    continueInFlightRef.current = true;
    setContinueLoading(true);
    clearCountdownInterval();

    try {
      const url = API_ENDPOINTS.CONTINUE_SUPPORT_SESSION.url.replace('{_Id}', userId);
      const response = await axiosInstance.put(url, {});
      const result = response.data;
      const responseCode = result?.response?.responseCode;

      if (isSessionExpiredResponse(responseCode)) {
        setWarningOpen(false);
        setSecondsRemaining(0);
        await handleSupportSessionExpired();
        return;
      }

      // Backend still returns warning (TTL <= threshold) - restart countdown with new TTL
      if (isSessionWarningResponse(responseCode)) {
        const details = result?.details || {};
        const ttl = Number(details.sessionSecondsRemaining) || 60;
        expiresAtRef.current = Date.now() + ttl * 1000;
        setSecondsRemaining(ttl);
        setCountdownRestartKey((k) => k + 1);
        return;
      }

      // Success - session extended, close dialog and schedule next sync
      if (isSessionActivitySuccess(result)) {
        setWarningOpen(false);
        setSecondsRemaining(0);
        const details = result?.details || result?.info || {};
        const ttl = Number(details.sessionSecondsRemaining) || 900;
        const syncLead = Number(details.sessionTtlSyncLeadSeconds) || syncLeadSecondsRef.current;
        scheduleSyncTimer({ sessionSecondsRemaining: ttl, sessionTtlSyncLeadSeconds: syncLead });
      }
    } catch (error) {
      const errorCode = parseSessionActivityResponse(error?.response?.data);
      if (isSessionExpiredResponse(errorCode)) {
        setWarningOpen(false);
        setSecondsRemaining(0);
        await handleSupportSessionExpired();
        return;
      }
      // On error, restart countdown with remaining seconds
      const ttl = secondsRemaining > 0 ? secondsRemaining : 60;
      expiresAtRef.current = Date.now() + ttl * 1000;
      setSecondsRemaining(ttl);
      setCountdownRestartKey((k) => k + 1);
    } finally {
      continueInFlightRef.current = false;
      setContinueLoading(false);
    }
  }, [clearCountdownInterval, scheduleSyncTimer, secondsRemaining]);

  /**
   * Watch for another agent asking to take over the account being impersonated.
   * Read-only on the server, so polling never resets the 15-minute inactivity timer.
   */
  const pollImpersonationRequest = useCallback(async () => {
    // Never overwrite the dialog state while an Allow / Deny call is still in flight.
    if (decisionInFlightRef.current) return;
    if (!shouldMonitorSupportSessionActivity() || !isImpersonatingCustomer()) {
      setIncomingRequest(null);
      return;
    }
    const userId = getSupportSessionUserId();
    if (!userId) return;

    try {
      const url = API_ENDPOINTS.GET_IMPERSONATION_REQUEST_STATUS.url.replace('{_Id}', userId);
      const response = await axiosInstance.get(url);
      const result = response.data;
      const responseCode = String(result?.response?.responseCode ?? '');
      const details = result?.details || {};

      if (responseCode === IMPERSONATION_PENDING_CODE && details.impersonationRole === 'owner') {
        setIncomingRequest({
          requestId: details.impersonationRequestId,
          name: fullName(details.requestingSupportUserFirstName, details.requestingSupportUserLastName),
          email: details.requestingSupportUserEmail || ''
        });
        return;
      }
      // Nothing pending any more (withdrawn, expired, or already decided elsewhere).
      setIncomingRequest(null);
    } catch (error) {
      console.warn('[SupportSession] impersonationstatus failed:', error?.message || 'Unknown error');
    }
  }, []);

  useEffect(() => {
    const intervalId = setInterval(() => {
      void pollImpersonationRequest();
    }, IMPERSONATION_POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [pollImpersonationRequest]);

  const handleImpersonationDecision = useCallback(
    async (allow) => {
      if (decisionInFlightRef.current || !incomingRequest?.requestId) return;
      const userId = getSupportSessionUserId();
      if (!userId) return;

      decisionInFlightRef.current = true;
      setDecisionLoading(true);
      try {
        const url = API_ENDPOINTS.DECIDE_IMPERSONATION_REQUEST.url.replace('{_Id}', userId);
        const response = await axiosInstance.put(url, {
          requestId: incomingRequest.requestId,
          decision: allow ? '1' : '0'
        });
        setIncomingRequest(null);

        // On approval the server has already moved the session to the other agent.
        if (allow && isSessionActivitySuccess(response.data)) {
          clearSyncTimeout();
          clearCountdownInterval();
          leaveImpersonatedAccount();
        }
      } catch (error) {
        // A rejected 44030 means this agent's session is gone; the interceptor already signals expiry.
        console.warn('[SupportSession] impersonationdecision failed:', error?.message || 'Unknown error');
        setIncomingRequest(null);
      } finally {
        decisionInFlightRef.current = false;
        setDecisionLoading(false);
      }
    },
    [incomingRequest, clearSyncTimeout, clearCountdownInterval]
  );

  useEffect(() => {
    const handleTtlUpdate = (ttlInfo) => scheduleSyncTimer(ttlInfo);

    const handleWarning = (data) => {
      expiresAtRef.current = Date.now() + (data.sessionSecondsRemaining || 0) * 1000;
      setWarningOpen(true);
      setSecondsRemaining(data.sessionSecondsRemaining || 0);
      if (data.sessionTtlSyncLeadSeconds) {
        syncLeadSecondsRef.current = data.sessionTtlSyncLeadSeconds;
      }
    };

    const handleExpired = () => {
      clearSyncTimeout();
      clearCountdownInterval();
      setWarningOpen(false);
      setSecondsRemaining(0);
      void handleSupportSessionExpired();
    };

    const handleBootstrap = () => void bootstrapSupportSession();

    dialogEventEmitter.on('supportSessionTtlUpdate', handleTtlUpdate);
    dialogEventEmitter.on('supportSessionWarning', handleWarning);
    dialogEventEmitter.on('supportSessionExpired', handleExpired);
    dialogEventEmitter.on('supportSessionBootstrap', handleBootstrap);

    return () => {
      dialogEventEmitter.off('supportSessionTtlUpdate', handleTtlUpdate);
      dialogEventEmitter.off('supportSessionWarning', handleWarning);
      dialogEventEmitter.off('supportSessionExpired', handleExpired);
      dialogEventEmitter.off('supportSessionBootstrap', handleBootstrap);
      clearSyncTimeout();
      clearCountdownInterval();
    };
  }, [scheduleSyncTimer, clearSyncTimeout, clearCountdownInterval, bootstrapSupportSession]);

  // Bootstrap timer when support user lands on own Insight account (mspHome) or refreshes page
  useEffect(() => {
    void bootstrapSupportSession();
  }, [bootstrapSupportSession]);

  const handleCountdownExpired = useCallback(async () => {
    void handleSupportSessionExpired();
  }, []);

  // Prints the dialog's countdown beside the server's own, once a second while the dialog is up.
  useEffect(() => {
    if (!warningOpen || !isTimerLogEnabled()) return undefined;

    const intervalId = setInterval(async () => {
      const userId = getSupportSessionUserId();
      if (!userId) return;
      const frontend = expiresAtRef.current ? Math.max(0, Math.ceil((expiresAtRef.current - Date.now()) / 1000)) : 0;
      let backend = 'n/a';
      let code = 'n/a';
      try {
        const data = await readBackendTtl(userId);
        code = String(data?.response?.responseCode ?? 'n/a');
        const details = data?.details || {};
        if (details.sessionSecondsRemaining !== undefined) {
          backend = Number(details.sessionSecondsRemaining);
        }
      } catch (error) {
        code = `ERROR:${error?.message || 'unknown'}`;
      }
      const drift = 'n/a' === backend ? 'n/a' : `${frontend - backend}s`;
      // Opt-in debug logger (see isTimerLogEnabled): printing is its whole purpose.
      // eslint-disable-next-line no-console
      console.log(`[SessionTimer] frontend=${frontend}s | backend=${backend} | drift=${drift} | code=${code}`);
    }, TIMER_LOG_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [warningOpen]);

  useEffect(() => {
    clearCountdownInterval();
    if (!warningOpen || secondsRemaining <= 0) return;

    countdownIntervalRef.current = setInterval(() => {
      // Read off the deadline, not prev - 1: an interval only ever fires late, so counting ticks
      // drifts in the one direction that shows more time than the session has left.
      const remaining = expiresAtRef.current ? Math.max(0, Math.ceil((expiresAtRef.current - Date.now()) / 1000)) : 0;
      setSecondsRemaining(remaining);
      if (remaining <= 0) {
        clearCountdownInterval();
        void handleCountdownExpired();
      }
    }, LOCAL_COUNTDOWN_INTERVAL_MS);

    return () => clearCountdownInterval();
    // secondsRemaining is read only as the start guard; depending on it would tear down and
    // restart this 1s interval on every tick it produces. countdownRestartKey re-arms it instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warningOpen, countdownRestartKey, clearCountdownInterval, handleCountdownExpired]);

  // Auth portal: silent refresh every 5 minutes (no expiry endpoint; invisible to user)
  useEffect(() => {
    if (!isAuthPortal) return;
    startAuthPortalSilentRefresh();
    return () => {
      stopAuthPortalSilentRefresh();
    };
  }, [isAuthPortal]);

  // Cognito: monitor expiry and refresh 2 minutes before
  useEffect(() => {
    if (isAuthPortal) return;
    const accessToken = localStorage.getItem('token') || localStorage.getItem('access_token');
    const refreshToken = localStorage.getItem('refreshtoken') || localStorage.getItem('refresh_token');

    if (accessToken && refreshToken) {
      startTokenRefreshMonitor();
    }

    return () => {
      stopTokenRefreshMonitor();
    };
  }, [isAuthPortal]);

  // Monitor for token changes (e.g., after login) - Cognito only
  useEffect(() => {
    if (isAuthPortal) return;
    const handleStorageChange = () => {
      const accessToken = localStorage.getItem('token') || localStorage.getItem('access_token');
      const refreshToken = localStorage.getItem('refreshtoken') || localStorage.getItem('refresh_token');

      if (accessToken && refreshToken) {
        if (!window.tokenRefreshActive) {
          window.tokenRefreshActive = true;
          startTokenRefreshMonitor();
        }
      } else {
        window.tokenRefreshActive = false;
        stopTokenRefreshMonitor();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    const checkInterval = setInterval(() => {
      const accessToken = localStorage.getItem('token') || localStorage.getItem('access_token');
      const refreshToken = localStorage.getItem('refreshtoken') || localStorage.getItem('refresh_token');

      const hasTokens = !!(accessToken && refreshToken);
      const isMonitoring = !!window.tokenRefreshActive;

      if (hasTokens && !isMonitoring) {
        window.tokenRefreshActive = true;
        startTokenRefreshMonitor();
      } else if (!hasTokens && isMonitoring) {
        window.tokenRefreshActive = false;
        stopTokenRefreshMonitor();
      }
    }, 5000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(checkInterval);
      stopTokenRefreshMonitor();
      window.tokenRefreshActive = false;
    };
  }, [isAuthPortal]);

  // Render support session warning dialog and incoming hand-over request dialog
  return (
    <>
      <Dialog
        open={warningOpen}
        aria-labelledby="support-session-warning-title"
        aria-describedby="support-session-warning-description"
        disableEscapeKeyDown
      >
        <DialogTitle id="support-session-warning-title">
          <FormattedMessage id="supportSessionWarningTitle" />
        </DialogTitle>
        <DialogContent id="support-session-warning-description">
          <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center' }}>
            <FormattedMessage id="supportSessionWarningMessage" values={{ seconds: secondsRemaining }} />
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2, px: 3 }}>
          <Button
            id="support-session-warning-continue-button"
            variant="contained"
            onClick={handleContinueSession}
            disabled={continueLoading}
          >
            <FormattedMessage id="supportSessionWarningContinue" />
          </Button>
        </DialogActions>
      </Dialog>

      {/* Another support agent is asking to take over this impersonation session */}
      <Dialog
        open={Boolean(incomingRequest)}
        aria-labelledby="impersonation-access-request-title"
        aria-describedby="impersonation-access-request-description"
        disableEscapeKeyDown
      >
        <DialogTitle id="impersonation-access-request-title">
          <FormattedMessage id="impersonationAccessRequestTitle" defaultMessage="Impersonation Access Request" />
        </DialogTitle>
        <DialogContent id="impersonation-access-request-description">
          <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center' }}>
            <FormattedMessage
              id="impersonationAccessRequestMessage"
              defaultMessage="{name} ({email}) is requesting permission to impersonate this customer account."
              values={{ name: incomingRequest?.name, email: incomingRequest?.email }}
            />
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', mt: 1.5 }}>
            <FormattedMessage
              id="impersonationAccessRequestQuestion"
              defaultMessage="Would you like to allow {name} to take over the impersonation session?"
              values={{ name: incomingRequest?.name }}
            />
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2, px: 3, gap: 1 }}>
          <Button
            id="impersonation-access-request-deny-button"
            variant="outlined"
            color="error"
            onClick={() => handleImpersonationDecision(false)}
            disabled={decisionLoading}
          >
            <FormattedMessage id="impersonationDeny" defaultMessage="Deny" />
          </Button>
          <Button
            id="impersonation-access-request-allow-button"
            variant="contained"
            onClick={() => handleImpersonationDecision(true)}
            disabled={decisionLoading}
          >
            <FormattedMessage id="impersonationAllow" defaultMessage="Allow" />
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
